import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import Sparkline, { withTrailingMean } from "../common/Sparkline";
import InfoTooltip from "../common/InfoTooltip";
import { useNavigateToMonitoredUri } from "../CurrentMonitoredUri/UriLink";
import FixtureGate from "../Changes/FixtureGate";
import { useFixtureWindow } from "./fixture";
import { useIssuesFixture } from "../Issues/fixture";
import IssueList from "../Issues/IssueList";
import { KINDS, titleOf } from "../Issues/kinds";
import { SegmentedRow, SegmentedButton } from "../common/SegmentedButtons";
import { usePushParams } from "../CurrentMonitoredUri/commons";
import { siteShareOf, TRAFFIC_LOST_HELP, PIVOT_COLOR, PIVOT_HELP, formatPivotDay } from "../common/trafficEstimates";

const MAX_ROWS = 50;

/** `?issues=`: any open issue, or one kind of issue (Issues/kinds.js). */
const ISSUE_FILTERS = ["open", ...KINDS.map((kind) => kind.key)];

const formatDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

const daysFrom = (start, count) =>
    Array.from({ length: count }, (_, index) => new Date(Date.parse(`${start}T00:00:00Z`) + index * 86400000).toISOString().slice(0, 10));

/** A drop-off is a pivot whose magnitude is negative (pivot.md). */
const hasDropOff = (page) => page.pivot && page.pivot.magnitude < 0;

const byLoss = (a, b) => b.trafficLost.lost - a.trafficLost.lost;

export default function Traffic() {
    const navigateToMonitoredUri = useNavigateToMonitoredUri();
    const { dayWindow, error } = useFixtureWindow();
    const { fixture: issuesFixture, error: issuesError } = useIssuesFixture();
    const location = useLocation();
    const pushParams = usePushParams();
    const params = new URLSearchParams(location.search);
    const issueFilter = ISSUE_FILTERS.includes(params.get("issues")) ? params.get("issues") : null;

    if (!dayWindow) return <FixtureGate error={error} />;
    if (!issuesFixture) {
        return <FixtureGate error={issuesError} />;
    }

    const issuesByUri = new Map(issuesFixture.pages.map((page) => [page.uri, page.issues]));

    const withSessions = dayWindow.paths.filter((page) => page.spark.some((sessions) => sessions > 0));
    const dropping = withSessions.filter(hasDropOff).sort(byLoss);
    const matching = (filter) => dropping.filter((page) => {
        const issues = issuesByUri.get(page.uri);
        return filter === "open" ? Boolean(issues) : (issues || []).some((issue) => issue.kind === filter);
    });
    const listed = issueFilter ? matching(issueFilter) : dropping;
    const rows = listed.slice(0, MAX_ROWS);
    const days = daysFrom(dayWindow.start, dayWindow.paths[0]?.spark.length ?? 0);

    return (
        <Wrap>
            <Header>
                <Title>Pages losing traffic</Title>
                <Subtitle>
                    {dropping.length.toLocaleString()} of {withSessions.length.toLocaleString()} pages with sessions have a
                    drop-off · {formatDate(days[0])} – {formatDate(days[days.length - 1])}
                </Subtitle>
            </Header>

            <TableBar>
                <TableTitle>
                    {issueFilter === "open" ? "Drop-offs with an open issue" : issueFilter ? `${titleOf(issueFilter)} — drop-offs` : "Drop-offs"} by traffic lost — {listed.length > MAX_ROWS
                        ? `the first ${MAX_ROWS} of ${listed.length.toLocaleString()}`
                        : listed.length.toLocaleString()}
                </TableTitle>
                <Controls>
                    <IssueFilterRow>
                        <SegmentedButton $active={!issueFilter} onClick={() => pushParams({ issues: null })}>All</SegmentedButton>
                        {ISSUE_FILTERS.map((filter) => (
                            <FilterButton key={filter} $active={filter === issueFilter} onClick={() => pushParams({ issues: filter })}>
                                {filter === "open" ? "Any issue" : titleOf(filter)}<Count>{matching(filter).length}</Count>
                            </FilterButton>
                        ))}
                    </IssueFilterRow>
                </Controls>
            </TableBar>

            <Table>
                <HeaderRow>
                    <span>Page</span>
                    <span>Issues</span>
                    <span><NoWrap>Est. traffic lost <InfoTooltip text={TRAFFIC_LOST_HELP} /></NoWrap><Unit>sessions/month</Unit></span>
                    <span><NoWrap>Pivot <InfoTooltip text={PIVOT_HELP} /></NoWrap></span>
                    <span>Organic sessions, 12 months</span>
                </HeaderRow>

                {rows.map((page) => {
                    const issues = issuesByUri.get(page.uri);
                    const markers = (issues || [])
                        .map((issue) => issue.since)
                        .filter((day) => day && day >= days[0] && day <= days[days.length - 1]);
                    return (
                        <Line key={page.uri}>
                            <PageCell onClick={() => navigateToMonitoredUri(page.uri)} title={page.uri}>
                                <PathLine>
                                    <Path>{page.path}</Path>
                                    <ExternalLink href={page.uri} target="_blank" rel="noopener noreferrer" title="Open in new tab" onClick={(event) => event.stopPropagation()}>
                                        <OpenInNewIcon style={{ fontSize: "0.75rem" }} />
                                    </ExternalLink>
                                </PathLine>
                            </PageCell>

                            {issues ? <IssueList issues={issues} /> : <Quiet>no open issue</Quiet>}

                            <Figure title={siteShareOf(page.spark, dayWindow.traffic)}>
                                <Big>~{compact.format(page.trafficLost.lost * 30)}</Big>
                                <PhoneUnit> sessions/month lost</PhoneUnit>
                            </Figure>

                            <PivotCell>
                                <PivotDay>{formatPivotDay(page.pivot.day)}</PivotDay>
                                <Quiet>p {page.pivot.p.toExponential(0)}</Quiet>
                            </PivotCell>

                            <Chart>
                                <Sparkline data={withTrailingMean(page.spark, days)} color="#818cf8" average="#4338ca" markers={markers}
                                    points={[{ day: page.pivot.day, color: PIVOT_COLOR }]} fromZero scale />
                            </Chart>
                        </Line>
                    );
                })}
            </Table>

            <Foot>
                Pages whose pivot is a drop. Traffic lost is the average daily sessions in the 30 days before the pivot minus the 30 days from it, times 30.
            </Foot>
        </Wrap>
    );
}

const COLUMNS = "minmax(0, 1fr) minmax(0, 1.4fr) 120px 90px 220px";

const Wrap = styled.div`
    padding-bottom: 40px;
`;

const Header = styled.div`
    margin-bottom: 16px;
    @media (max-width: 600px) { padding: 0 16px; }
`;


const Title = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
`;

const Subtitle = styled.div`
    margin-top: 4px;
    font-size: 0.82rem;
    color: #64748b;
`;


const TableBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 8px 2px;
    @media (max-width: 600px) { padding: 0 16px; }
`;

const IssueFilterRow = styled(SegmentedRow)`
    flex-wrap: wrap;
`;

const FilterButton = styled(SegmentedButton)`
    white-space: nowrap;
`;

const Count = styled.span`
    margin-left: 4px;
    opacity: 0.6;
    font-variant-numeric: tabular-nums;
`;

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const TableTitle = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #64748b;
`;

const Table = styled.div`
    border: 1px solid #e8edf4;
    border-radius: 14px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
        box-shadow: none;
    }
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: ${COLUMNS};
    gap: 20px;
    padding: 10px 18px;
    border-bottom: 1px solid #eef2f7;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #b6c0ce;
    @media (max-width: 600px) { display: none; }
`;

const Line = styled.div`
    display: grid;
    grid-template-columns: ${COLUMNS};
    align-items: start;
    gap: 20px;
    padding: 14px 18px;
    border-bottom: 1px solid #f4f7fa;
    transition: background 120ms ease;
    &:last-child { border-bottom: none; }
    &:hover { background: #fbfcfe; }
    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);
        row-gap: 6px;
    }
`;

const PageCell = styled.div`
    min-width: 0;
    cursor: pointer;
    &:hover > div > div:first-child { color: #4f46e5; }
`;

const PathLine = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const Path = styled.div`
    min-width: 0;
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: -0.1px;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 120ms ease;
`;

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    color: #cbd5e1;
    text-decoration: none;
    transition: background 120ms, color 120ms;
    &:hover { background: #ede9fe; color: #6366f1; }
`;

const Big = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
    color: #334155;
    font-variant-numeric: tabular-nums;
`;


const Quiet = styled.span`
    font-size: 0.72rem;
    color: #b6c0ce;
`;

const Chart = styled.div`
    height: 48px;
    min-width: 0;
    @media (max-width: 600px) { height: 44px; }
`;


const Figure = styled.div`
    white-space: nowrap;
`;

const NoWrap = styled.span`
    white-space: nowrap;
`;

const Unit = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
`;

/** The header carries the units; phones hide the header, so the row carries them there. */
const PhoneUnit = styled.span`
    display: none;
    font-size: 0.7rem;
    color: #a9b4c2;
    @media (max-width: 600px) { display: inline; }
`;

const PivotCell = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 8px;
    font-size: 0.78rem;
    color: #334155;
    font-variant-numeric: tabular-nums;
`;

const PivotDay = styled.span`
    font-weight: 700;
    color: ${PIVOT_COLOR};
`;

const Foot = styled.div`
    margin: 12px 2px 0;
    font-size: 0.74rem;
    color: #94a3b8;
    line-height: 1.55;
    @media (max-width: 600px) { padding: 0 16px; }
`;
